import { ChangeEvent, KeyboardEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'baseui/snackbar'

import useDevTools from '@/containers/DevToolsNew/shared/useDevTools'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'

import { betaFeaturesActions, MIN_SEARCH_LENGTH } from './betaFeaturesSagas'
import { selectBetaFeaturesTools } from './betaFeaturesSelectors'
import { askRemoval as askRemovalAction, setSearchQuery } from './betaFeaturesSlice'
import { formatAddedAt, fullNameOf } from './testerLabels'
import { BetaFeature, BetaFeatureTester, UserSearchResult } from './types'

const useBetaFeatureCard = (betaFeature: BetaFeature) => {
  const { feature, country, testers } = betaFeature
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { t } = useDevTools()
  const { searchQueries, searchResults, searchingFeature } = useSelector(selectBetaFeaturesTools)
  const query = searchQueries[feature] ?? ''
  const results = searchResults[feature]
  const testerIds = new Set(testers.map(({ user_id }) => user_id))
  const canSearch = query.trim().length >= MIN_SEARCH_LENGTH && searchingFeature !== feature

  const notifySuccess = (message: string) => () => showSnackbar(enqueue, { type: SNACKBAR_TYPES.SUCCESS, message })

  const askRemoval = (tester: BetaFeatureTester) =>
    dispatch(askRemovalAction({ feature, userId: tester.user_id, name: fullNameOf(tester) }))

  const addTester = (user: UserSearchResult) =>
    dispatch({
      type: betaFeaturesActions.ADD_TESTER,
      feature,
      userId: user.id,
      enqueue,
      onSuccess: notifySuccess(t('beta_features.added')),
    })

  const onSearch = () => {
    if (canSearch) dispatch({ type: betaFeaturesActions.SEARCH_CANDIDATES, feature, enqueue })
  }

  return {
    t,
    title: t(`beta_features.features.${feature}`, { defaultValue: feature }),
    countryLabel: t(`common.country_${country}`, { defaultValue: country }),
    testers: testers.map(tester => ({
      key: tester.user_id,
      fullName: fullNameOf(tester),
      email: tester.email ?? '',
      addedBy: tester.added_by ?? '',
      addedAt: formatAddedAt(tester.added_at),
      onRemove: () => askRemoval(tester),
    })),
    hasNoTesters: testers.length === 0,
    query,
    canSearch,
    isSearching: searchingFeature === feature,
    onQueryChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      dispatch(setSearchQuery({ feature, query: event.target.value }))
    },
    onSearchKeyDown: (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Enter') onSearch()
    },
    onSearch,
    results: (results ?? []).map(user => ({
      key: user.id,
      fullName: fullNameOf(user),
      email: user.email ?? '',
      isTester: testerIds.has(user.id),
      onAdd: () => addTester(user),
    })),
    hasResults: (results ?? []).length > 0,
    hasNoResults: results !== undefined && results.length === 0,
  }
}

export default useBetaFeatureCard
